// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, ColumnActionsMode, DetailsList, DetailsListLayoutMode, IColumn, IconButton, IDetailsHeaderProps, IDetailsRowProps, ScrollablePane, Selection, SelectionMode, Sticky, StickyPositionType } from "@fluentui/react";
import { userFontFamily } from "components/common/Fonts";
import { detailsListHeaderStyle } from "components/shell/presentation/AppStyles";
import { ColorIconProps } from "components/work_path/IconRenderer";
import { useDgIcons } from "hooks/dgIcons";
import { useMemo, useRef } from "react";
import styled from "styled-components";

const ListContainer = styled.div`
    flex: 1;
    position: relative;
    height: 100%;
`

const ItemName = styled.span`
    font-family: ${userFontFamily};
    font-size: 16px;
    font-weight: 100;
`;

const CellContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
`

const IconContainer = styled(CellContainer)`
    justify-content: center;
`

const Row = styled.div`
    .rowHoverVisible {
        visibility: hidden;
    }

    :hover {
        .rowHoverVisible {
            visibility: visible;
        }
    }
`

export interface ListItem {
    id: any;
    name: string;
    icon: ColorIconProps;
    autoSync?: boolean;
}

export interface AddDocsListViewProps {
    items: ListItem[];
    onSelectionChanged?: (items: ListItem[]) => void;
    selectionMode?: SelectionMode;
    onItemsDeleted?: (item: ListItem[]) => void;
    onSyncChanged?: (item: ListItem, isSynced: boolean) => void;
}

export const AddDocsListView = (props: AddDocsListViewProps): JSX.Element => {
    const icons = useDgIcons();
    const selectionRef = useRef<Selection<ListItem>>(new Selection({
        onSelectionChanged: () => onSelectionChanged(),
        getKey: item => item.id
    }));

    function onSelectionChanged() {
        props.onSelectionChanged?.(selectionRef.current.getSelection());
    }

    const columns: IColumn[] = useMemo(() => {
        const cols: IColumn[] = [
            {
                key: 'column1',
                name: 'File Type',
                iconName: '',
                isIconOnly: true,
                minWidth: 30,
                maxWidth: 30,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: ListItem) => {
                    return (
                        <IconContainer>
                            {
                                icons.icon(
                                    item.icon.name,
                                    '',
                                    item.icon.height,
                                    item.icon.width,
                                    item.icon.color,
                                    '')
                            }
                        </IconContainer>)
                }
            },
            {
                key: 'column2',
                name: 'Name',
                minWidth: 150,
                isRowHeader: true,
                isResizable: true,
                data: 'string',
                isPadded: true,
                styles: detailsListHeaderStyle,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: ListItem) => {
                    return (
                        <ItemName data-selection-disabled={true}>{item.name}</ItemName>
                    );
                }
            }
        ];

        if (!!props.onSyncChanged) {
            cols.push({
                key: 'column3',
                name: 'Auto Sync',
                isResizable: false,
                minWidth: 65,
                styles: detailsListHeaderStyle,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: ListItem) => {
                    if (item.autoSync !== undefined) {
                        return (
                            <div data-selection-disabled={true}>
                                <Checkbox
                                    styles={{ root: { width: "100%", display: "flex", justifyContent: "center" } }}
                                    checked={item.autoSync}
                                    onChange={(ev, isChecked) => props.onSyncChanged!(item, !!isChecked)} />
                            </div>
                        )
                    }

                    return <></>;
                }
            });
        }

        if (!!props.onItemsDeleted) {
            cols.push({
                key: 'column4',
                name: 'Overflow',
                isIconOnly: true,
                minWidth: 16,
                maxWidth: 16,
                styles: detailsListHeaderStyle,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: ListItem) => {
                    return (
                        <IconButton
                            data-selection-disabled={true}
                            styles={{ root: { maxHeight: "22px" }, rootHovered: { background: 'transparent' }, rootPressed: { background: 'transparent' } }}
                            className="rowHoverVisible"
                            iconProps={{ iconName: "Delete" }}
                            onClick={() => props.onItemsDeleted!([item])} />
                    )
                }
            })
        }

        return cols;

    }, [icons, props.onItemsDeleted, props.onSyncChanged])

    function renderHeader(props?: IDetailsHeaderProps, defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null): JSX.Element | null {
        if (!props || !defaultRender) {
            return null;
        }

        return (
            <Sticky stickyPosition={StickyPositionType.Header}>
                {defaultRender(props)}
            </Sticky>
        );
    }

    function renderRow(props?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null): JSX.Element | null {
        if (!props || !defaultRender) {
            return null;
        }

        return (
            <Row>
                {defaultRender(props)}
            </Row>
        );
    }

    return (
        <ListContainer>
            <ScrollablePane>
                <DetailsList
                    items={props.items}
                    columns={columns}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={props.selectionMode ?? (!!props.onSelectionChanged ? SelectionMode.single : SelectionMode.none)}
                    isHeaderVisible={true}
                    selectionPreservedOnEmptyClick={true}
                    onRenderDetailsHeader={renderHeader}
                    onRenderRow={renderRow}
                    setKey='set'
                    // @ts-ignore
                    selection={selectionRef.current}
                />
            </ScrollablePane>
        </ListContainer>
    )
}
